import { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Lightbulb, Zap, Target, ChevronDown, Flame, Send } from "lucide-react";
import { Header } from "@/components/neusim/Header";
import { BottomBar } from "@/components/neusim/BottomBar";
import { ResignationIllustration } from "@/components/neusim/ResignationIllustration";
import { ChoiceList, type Choice } from "@/components/neusim/ChoiceList";
import { ChoiceStatusBar } from "@/components/neusim/ChoiceStatusBar";

type VariantId = "early" | "mid";
type QuestionType = "OpenText" | "MultipleChoice";

interface Variant {
  id: VariantId;
  label: string;
  currentDecision: number;
  xp: number;
  timer: string;
  showHints: boolean;
}

const VARIANTS: readonly Variant[] = [
  { id: "early", label: "Early attempt", currentDecision: 1, xp: 30, timer: "19:48", showHints: false },
  { id: "mid", label: "Mid attempt", currentDecision: 6, xp: 280, timer: "04:12", showHints: true },
] as const;

const MIN_CHARS = 100;

interface SparkleBurst {
  id: number;
  x: number;
  y: number;
}

export function DecisionDemoPage(): JSX.Element {
  const [variantId, setVariantId] = useState<VariantId>("early");
  const [questionType, setQuestionType] = useState<QuestionType>("OpenText");
  const [selectedChoiceId, setSelectedChoiceId] = useState<string | null>(null);
  const [answer, setAnswer] = useState("");
  const [pasteWarning, setPasteWarning] = useState(false);
  const [expandedHint, setExpandedHint] = useState<number | null>(null);
  const [mood, setMood] = useState<string | null>(null);
  const [sparkles, setSparkles] = useState<SparkleBurst[]>([]);

  const variant = useMemo<Variant>(() => {
    const found = VARIANTS.find((v) => v.id === variantId);
    return found ?? VARIANTS[0]!;
  }, [variantId]);

  const progress = Math.min((answer.length / MIN_CHARS) * 100, 100);
  const reached = answer.length >= MIN_CHARS;
  const isMC = questionType === "MultipleChoice";

  useEffect(() => {
    if (reached && sparkles.length === 0) {
      const burst: SparkleBurst[] = Array.from({ length: 12 }).map((_, i) => ({
        id: Date.now() + i,
        x: Math.random() * 100,
        y: Math.random() * 100,
      }));
      setSparkles(burst);
      const t = window.setTimeout(() => setSparkles([]), 1500);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [reached, sparkles.length]);

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>): void => {
    e.preventDefault();
    setPasteWarning(true);
    window.setTimeout(() => setPasteWarning(false), 3000);
  };

  const moods = [
    { id: "decisive", label: "Decisive", emoji: "⚡", color: "from-amber-400 to-orange-500" },
    { id: "empathetic", label: "Empathetic", emoji: "💗", color: "from-pink-400 to-rose-500" },
    { id: "strategic", label: "Strategic", emoji: "🎯", color: "from-violet-400 to-fuchsia-500" },
    { id: "cautious", label: "Cautious", emoji: "🛡️", color: "from-cyan-400 to-sky-500" },
  ] as const;

  const hints = [
    {
      icon: Lightbulb,
      title: "Impact on the team",
      body: "Marcus leaving mid-sprint creates a knowledge gap. Who else can pair on the payments service? Consider morale — the room is watching how you react.",
      color: "from-amber-400 to-orange-500",
    },
    {
      icon: Target,
      title: "Timing matters",
      body: "Two weeks notice, three weeks to launch. Do you accelerate handover, renegotiate scope, or push the launch? Each path signals something different.",
      color: "from-fuchsia-400 to-pink-500",
    },
  ] as const;

  const mcChoices: Choice[] = [
    { id: "a", letter: "A", text: "Brief your team and sharpen the afternoon session. Diane will come around when she sees the quality of the work." },
    { id: "b", letter: "B", text: "Ask Raj to give you a read on Diane before approaching her directly." },
    { id: "c", letter: "C", text: "Find Diane. Ask what would make this engagement succeed unlike the failed ones. Listen only." },
    { id: "d", letter: "D", text: "Send Diane a brief message asking if she would be open to a short conversation later this week." },
  ];

  return (
    <div className="relative h-[100dvh] overflow-hidden bg-neusim-bg font-sans text-white">
      <motion.div
        aria-hidden
        animate={{ x: [0, 40, 0], y: [0, 20, 0] }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -left-40 -top-40 h-[700px] w-[700px] rounded-full"
        style={{ background: "#a855f7", opacity: 0.25, filter: "blur(180px)" }}
      />
      <motion.div
        aria-hidden
        animate={{ x: [0, -30, 0], y: [0, -25, 0] }}
        transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute -bottom-40 -right-40 h-[700px] w-[700px] rounded-full"
        style={{ background: "#ec4899", opacity: 0.22, filter: "blur(180px)" }}
      />
      <motion.div
        aria-hidden
        animate={{ x: [0, 20, 0], y: [0, -30, 0] }}
        transition={{ duration: 25, repeat: Infinity, ease: "easeInOut" }}
        className="pointer-events-none absolute right-1/3 top-1/3 h-[500px] w-[500px] rounded-full"
        style={{ background: "#22d3ee", opacity: 0.15, filter: "blur(180px)" }}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
      />

      <Header
        totalDecisions={10}
        currentDecision={variant.currentDecision}
        xp={variant.xp}
        timer={variant.timer}
      />

      <main className="absolute inset-x-0 top-16 bottom-[72px] mx-auto flex max-w-[1440px] flex-col overflow-hidden px-4 py-3 md:px-6 md:py-4">
        <div className="mb-3 flex flex-shrink-0 flex-wrap items-center justify-end gap-2">
          <div className="flex gap-1 rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur-xl">
            {VARIANTS.map((v) => (
              <button
                key={v.id}
                onClick={() => setVariantId(v.id)}
                className={`rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.08em] transition-colors ${
                  v.id === variantId
                    ? "bg-gradient-to-r from-fuchsia-500 to-violet-500 text-white shadow-lg shadow-fuchsia-500/30"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1 rounded-full border border-white/10 bg-white/5 p-1 backdrop-blur-xl">
            {(["OpenText", "MultipleChoice"] as const).map((q) => (
              <button
                key={q}
                onClick={() => setQuestionType(q)}
                className={`rounded-full px-3 py-1 text-[10px] font-medium uppercase tracking-[0.08em] transition-colors ${
                  q === questionType
                    ? "bg-gradient-to-r from-cyan-500 to-violet-500 text-white shadow-lg shadow-cyan-500/30"
                    : "text-white/60 hover:text-white"
                }`}
              >
                {q === "OpenText" ? "Open text" : "Multiple choice"}
              </button>
            ))}
          </div>
        </div>

        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 overflow-hidden md:grid-cols-[3fr_2fr] md:gap-6">
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="group relative min-h-0 min-w-0 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl"
          >
            <div className="absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-fuchsia-500 via-violet-500 to-cyan-400" />
            <motion.div
              aria-hidden
              className="pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full bg-fuchsia-500/20 blur-3xl"
              animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }}
              transition={{ duration: 6, repeat: Infinity }}
            />

            <div className="relative flex h-full flex-col overflow-y-auto overflow-x-hidden p-5 md:p-6">
            <div className="flex items-center gap-2">
              <Flame className="h-3.5 w-3.5 text-orange-400" />
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-orange-300">
                {isMC ? "Week 1 · Day 1 · Live Scenario" : "Week 2 · Day 3 · Live Scenario"}
              </span>
            </div>
            <h1 className="mt-2 bg-gradient-to-r from-white via-fuchsia-100 to-cyan-100 bg-clip-text text-2xl font-bold leading-tight text-transparent md:text-3xl">
              {isMC ? "The Difficult Stakeholder" : "The Resignation Letter"}
            </h1>
            <p className="mt-3 text-[13px] leading-[1.6] text-white/75 md:text-[14px] md:leading-[1.7]">
              {isMC
                ? "The kick-off just ended. It did not go well. Diane, the client's Head of Operations, sat through your team's introduction without engaging. Her only contribution was a sharp question about whether consultants like yours actually deliver. She did not acknowledge the response. Priti, your team lead, tells you afterwards: 'Diane has seen off two consulting firms. She has already decided we are the same. Let it go.' You have 30 minutes before the afternoon session. Diane is still in the building. What do you do?"
                : "It's Wednesday morning, mid-sprint. Marcus, your senior backend engineer and the only person who deeply understands the payments service, just dropped a resignation letter on your desk. He's leaving in two weeks. The team launch is in three. He's quiet, professional, and clearly already decided. The room is waiting for your reaction."}
            </p>

            <motion.div
              whileHover={{ scale: 1.02 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="relative mt-4 flex max-h-[22vh] w-full justify-center overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-violet-500/10 via-fuchsia-500/5 to-cyan-500/10 p-3 md:p-4 [&_svg]:h-auto [&_svg]:max-h-full [&_svg]:max-w-full [&_svg]:w-auto"
            >
              <ResignationIllustration />
            </motion.div>

            {!isMC && (
              <div className="mt-4">
                <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-white/50">
                  How does this make you feel?
                </span>
                <div className="mt-2 flex flex-wrap gap-2">
                  {moods.map((m) => (
                    <motion.button
                      key={m.id}
                      whileHover={{ scale: 1.05, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => setMood(m.id)}
                      className={`group/mood flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-medium transition-all ${
                        mood === m.id
                          ? `border-transparent bg-gradient-to-r ${m.color} text-white shadow-lg`
                          : "border-white/15 bg-white/5 text-white/70 hover:border-white/30 hover:text-white"
                      }`}
                    >
                      <span className="text-base">{m.emoji}</span>
                      {m.label}
                    </motion.button>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 flex items-center gap-3">
              <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-white/50">
                Competency
              </span>
              <motion.span
                animate={{
                  boxShadow: [
                    "0 0 0 0 rgba(236,72,153,0.4)",
                    "0 0 0 8px rgba(236,72,153,0)",
                    "0 0 0 0 rgba(236,72,153,0)",
                  ],
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="rounded-full border border-fuchsia-400/40 bg-gradient-to-r from-fuchsia-500/20 to-violet-500/20 px-3 py-1 text-xs font-semibold text-fuchsia-100"
              >
                {isMC ? "🤝 Influencing & Collaboration" : "⚡ Decisive Action"}
              </motion.span>
            </div>
            </div>
          </motion.section>

          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="relative min-h-0 min-w-0 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-white/[0.07] to-white/[0.02] backdrop-blur-xl"
          >
            <div className="absolute inset-x-0 top-0 z-10 h-1 bg-gradient-to-r from-cyan-400 via-violet-500 to-fuchsia-500" />
            <motion.div
              aria-hidden
              className="pointer-events-none absolute -bottom-20 -left-20 h-60 w-60 rounded-full bg-cyan-500/20 blur-3xl"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.6, 0.3] }}
              transition={{ duration: 5, repeat: Infinity }}
            />

            <div className="relative flex h-full flex-col overflow-y-auto overflow-x-hidden p-5 md:p-6">
            <div className="flex items-center justify-between">
              <motion.div
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
                className="inline-flex items-center gap-1.5 rounded-full border border-cyan-400/30 bg-gradient-to-r from-cyan-500/20 to-violet-500/20 px-3 py-1"
              >
                <Sparkles className="h-3 w-3 text-cyan-300" />
                <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-cyan-100">
                  AI Evaluated
                </span>
              </motion.div>
              {!isMC && (
                <div className="flex items-center gap-1 font-mono text-[10px] text-white/60">
                  <motion.span
                    key={answer.length}
                    initial={{ scale: 1.3, color: "#22d3ee" }}
                    animate={{ scale: 1, color: reached ? "#10b981" : "#ffffff99" }}
                    transition={{ duration: 0.3 }}
                    className="font-semibold"
                  >
                    {answer.length}
                  </motion.span>
                  <span>/ {MIN_CHARS}</span>
                </div>
              )}
            </div>

            <label className="mt-3 block text-[10px] font-medium uppercase tracking-[0.12em] text-white/50">
              Your Decision
            </label>

            {!isMC && (
              <>
                <div className="relative mt-2">
                  <textarea
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    onPaste={handlePaste}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onDrop={(e) => e.preventDefault()}
                    onContextMenu={(e) => e.preventDefault()}
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="Type at least 100 characters describing what you would do..."
                    className="h-[clamp(110px,calc(100dvh-560px),200px)] w-full resize-none rounded-xl border border-white/10 bg-neusim-bg/60 p-3 text-sm leading-relaxed text-white placeholder:text-white/30 transition-all duration-200 focus:border-fuchsia-400/60 focus:shadow-[0_0_0_4px_rgba(236,72,153,0.15)] focus:outline-none"
                  />
                  <AnimatePresence>
                    {sparkles.map((s) => (
                      <motion.div
                        key={s.id}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: [0, 1, 0], scale: [0, 1.5, 0], y: -30 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.2 }}
                        className="pointer-events-none absolute"
                        style={{ left: `${s.x}%`, top: `${s.y}%` }}
                      >
                        <Sparkles className="h-4 w-4 text-fuchsia-300" />
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
                <div className="mt-2 flex items-center justify-between gap-3">
                  <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/5">
                    <motion.div
                      className="h-full rounded-full bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-amber-400"
                      animate={{ width: `${progress}%` }}
                      transition={{ duration: 0.3 }}
                    />
                  </div>
                  <span className="font-mono text-[10px] text-white/50">
                    {reached ? (
                      <span className="flex items-center gap-1 font-semibold text-neusim-band-high">
                        <Zap className="h-3 w-3" /> Ready · +50 XP
                      </span>
                    ) : (
                      `${answer.length} / ${MIN_CHARS}`
                    )}
                  </span>
                </div>

                <AnimatePresence>
                  {pasteWarning && (
                    <motion.p
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="mt-2 rounded-md border border-rose-400/30 bg-rose-500/10 px-3 py-2 text-xs text-rose-300"
                    >
                      ⚠ Pasting is not allowed — type your own answer
                    </motion.p>
                  )}
                </AnimatePresence>

                <div className="mt-3 space-y-1.5">
                  {hints.map((h, i) => {
                    const open = expandedHint === i || variant.showHints;
                    const Icon = h.icon;
                    return (
                      <motion.div
                        key={i}
                        layout
                        className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]"
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedHint(open ? null : i)}
                          className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.04]"
                        >
                          <div className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br ${h.color}`}>
                            <Icon className="h-3.5 w-3.5 text-white" />
                          </div>
                          <span className="flex-1 text-xs font-medium text-white/90">{h.title}</span>
                          <motion.span animate={{ rotate: open ? 180 : 0 }}>
                            <ChevronDown className="h-4 w-4 text-white/50" />
                          </motion.span>
                        </button>
                        <AnimatePresence initial={false}>
                          {open && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.2 }}
                            >
                              <p className="px-4 pb-3 text-xs leading-relaxed text-white/65">{h.body}</p>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    );
                  })}
                </div>
              </>
            )}

            {isMC && (
              <div className="mt-2">
                <ChoiceStatusBar
                  selected={selectedChoiceId !== null}
                  hintText="Choose one option to continue"
                />
                <ChoiceList
                  choices={mcChoices}
                  selectedId={selectedChoiceId}
                  onSelect={setSelectedChoiceId}
                />
              </div>
            )}
            </div>
          </motion.section>
        </div>
      </main>

      <BottomBar
        primaryLabel="Submit Decision"
        primaryIcon={Send}
        disabled={isMC ? selectedChoiceId === null : answer.length < MIN_CHARS}
        onPrimary={() => {
          // Phase 2 will wire this to /api/evaluate
        }}
      />
    </div>
  );
}
